import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { getAccessToken, useAuthStore } from '../store/auth.store'

const API_BASE_URL: string = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_API_URL ?? '/api/v1'

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  withCredentials: true, // For httpOnly Cookie (Refresh Token)
})

function newRequestId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // fall through to fallback below
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

// Request interceptor: add in-memory JWT to every request
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken() ?? useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  if (!config.headers['X-Request-ID']) {
    config.headers['X-Request-ID'] = newRequestId()
  }
  return config
})

/** Axios request config flagged for the refresh-retry logic (no `any`). */
interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

interface QueuedRequest {
  resolve: (token: string) => void
  reject: (reason?: unknown) => void
}

// Response interceptor: automatically refresh token
let isRefreshing = false
let failedQueue: QueuedRequest[] = []

function processQueue(error: unknown, token: string | null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error || !token) {
      reject(error)
    } else {
      resolve(token)
    }
  })
  failedQueue = []
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined

    // Network error (no config to retry): reject immediately.
    if (!original) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !original._retry) {
      if (isRefreshing) {
        // Queue behind the in-flight refresh; retry with the NEW token, not the stale header.
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              original.headers.Authorization = `Bearer ${token}`
              resolve(apiClient(original))
            },
            reject,
          })
        })
      }

      original._retry = true
      isRefreshing = true

      try {
        // Plain axios (not apiClient) to avoid re-entering this interceptor.
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true })
        const newToken = data.access_token as string
        useAuthStore.getState().setTokens(newToken)

        // Apply the fresh token to the original request before retrying.
        // Queued requests already receive it via processQueue -> resolve(token).
        original.headers.Authorization = `Bearer ${newToken}`
        processQueue(null, newToken)

        return apiClient(original)
      } catch (err) {
        processQueue(err, null)
        useAuthStore.getState().logout()
        window.location.href = '/login'
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default apiClient
export { API_BASE_URL }
